/* eslint-disable @typescript-eslint/no-floating-promises */
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { type RootState } from "../../store";
import {
  type MidserviceType,
  type FullInstanceType,
  type RequestLogsType,
  type MidserviceStatus,
  type SendConfigType,
  type InstanceConfigType,
  type InstanceStatusSchema,
  type SendCommandParamsType,
  type MessageType,
  type WebosocketMessageType,
  type Notification,
  type PaginationResponse,
  type PaginationParams,
  type IssueType,
  type FullIssueType,
  type SendCommandToDeviceListType,
} from "./midserviceTypes";
import { API } from "../../../api/Api";
import { message, notification } from "antd";
// import { TelemetryAPI } from "../../../api/TelemetryApi";

type Status = "idle" | "loading" | "failed" | "success";

export interface InstanceState {
  status: Status;
  logsStatus: Status;
  sendConfigStatus: Status;

  errorCode: number | null;
  instances: PaginationResponse<MidserviceType[]>;
  issues: PaginationResponse<IssueType[]>;
  fullInstance?: FullInstanceType;
  fullIssue?: FullIssueType;
  newLogs: number;
  newImages: number;
  loading: boolean;
  instancesListCurrentPage: number;
  issuesListCurrentPage: number;
  pageReload: boolean;
}

const initialState: InstanceState = {
  errorCode: null,
  status: "idle",
  logsStatus: "idle",
  sendConfigStatus: "idle",
  instances: {
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecord: 0,
    content: [],
  },
  issues: {
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecord: 0,
    content: [],
  },
  loading: false,
  instancesListCurrentPage: 1,
  issuesListCurrentPage: 1,
  newLogs: 0,
  newImages: 0,
  pageReload: false,
};

export const midserviceInstansesListAsync = createAsyncThunk(
  "midservice/midserviceInstancesList",
  async (instancesListParams: PaginationParams) => {
    console.log("midserviceInstansesListAsync");
    const response = await API.getMidserviceInstancesList(instancesListParams);
    console.log("responseresponseresponse", response);
    return response.data;
  },
);

export const issuesListAsync = createAsyncThunk(
  "midservice/issuesList",
  async (issuesListParams: PaginationParams) => {
    console.log("issuesList");
    const response = await API.getIssuesList(issuesListParams);
    console.log("issueresponse", response.data);
    return response.data;
  },
);

export const fullIssueAsync = createAsyncThunk(
  "midservice/singleIssue",
  async (issueId: number) => {
    console.log("singleIssueAsync");
    const response = await API.getSingleIssue(issueId);
    return response.data;
  },
);

export const fullInstanceAsync = createAsyncThunk(
  "midservice/singleInstance",
  async (midserviceId: number) => {
    console.log("singleMidserviceAsync");
    const response = await API.getSingleInstance(midserviceId);
    return response.data;
  },
);

export const requestLogsAsync = createAsyncThunk(
  "midservice/requestLogs",
  async (getLogsRequestParams: RequestLogsType, { rejectWithValue }) => {
    console.log("getLogsAsync");
    try {
      const response = await API.requestLogs(getLogsRequestParams);
      console.log("getLogsAsync", response);
      return response.data;
    } catch (error: any) {
      // return custom error message from API if any

      if (error?.response?.data?.detail !== null) {
        return rejectWithValue(error.response.data.detail);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export const restartAsync = createAsyncThunk(
  "midservice/restart",
  async (
    { domain, deviceId }: { domain: string; deviceId: string },
    { rejectWithValue },
  ) => {
    console.log("deleteLogsAsync");
    try {
      const response = await API.restartInstance(domain, deviceId);
      console.log("deleteLogsAsync resp", response);
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.detail !== null) {
        return rejectWithValue(error.response.data.detail);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export const deleteLogsAsync = createAsyncThunk(
  "midservice/deleteLogsAsync",
  async (
    { domain, deviceId }: { domain: string; deviceId: string },
    { rejectWithValue },
  ) => {
    console.log("deleteLogsAsync");
    try {
      const response = await API.deleteLogs(domain, deviceId);
      console.log("deleteLogsAsync resp", response);
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.detail !== null) {
        return rejectWithValue(error.response.data.detail);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export const sendConfigAsync = createAsyncThunk(
  "midservice/sendConfig",
  async (sendConfigParams: SendConfigType, { rejectWithValue, dispatch }) => {
    console.log("sendConfigAsync");
    try {
      dispatch(setConfig(sendConfigParams.config));
      const response = await API.postConfig(
        sendConfigParams.domain,
        sendConfigParams.deviceId,
        sendConfigParams.config,
      );
      console.log("getLogsAsync", response);
      return response.data;
    } catch (error: any) {
      if (error.response.data.message !== null) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

// deletes instance
export const deleteInstanceAsync = createAsyncThunk(
  "midservice/deleteInstance",
  async (instanceId: number, { rejectWithValue, dispatch }) => {
    console.log("deleteInstanceAsync");
    try {
      const response = await API.deleteInstance(instanceId);
      console.log("deleteInstanceAsync", response);
      return response.data;
    } catch (error: any) {
      if (error.response.data.message !== null) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export const sendCommandAsync = createAsyncThunk(
  "midservice/sendCommand",
  async (
    sendCommandParams: SendCommandParamsType,
    { rejectWithValue, dispatch },
  ) => {
    console.log("sendCommand");
    try {
      dispatch(addMessage(sendCommandParams.message));
      const response = await API.sendCommand(
        sendCommandParams.domain,
        sendCommandParams.deviceId,
        sendCommandParams.message,
      );
      console.log("sendCommand", response);
      return response.data;
    } catch (error: any) {
      if (error.response.data.message !== null) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export const sendCommandToDeviceListAsync = createAsyncThunk(
  "midservice/sendCommandToDeviceList",
  async (
    sendCommandParams: SendCommandToDeviceListType,
    { rejectWithValue },
  ) => {
    console.log("sendCommandToDeviceList");
    try {
      const response = await API.sendCommandsToDeviceList(
        sendCommandParams.message,
        sendCommandParams.devices,
        sendCommandParams.search,
      );
      console.log("sendCommandToDeviceList", response);
      return response.data;
    } catch (error: any) {
      if (error.response.data.message !== null) {
        return rejectWithValue(error.response.data.message);
      } else {
        return rejectWithValue(error.message);
      }
    }
  },
);

export interface ChangeOnlineStatusProps {
  deviceId: string;
  onlineStatus: MidserviceStatus;
}

const openNotification = (notificationMessage: Notification): void => {
  notification[notificationMessage.notificationType]({
    duration: 0,
    message: notificationMessage.message,
    description: notificationMessage.description,
    onClick: () => {
      console.log("Notification Clicked!");
    },
  });
};

export const midserviceSlice = createSlice({
  name: "midservice",
  initialState,
  reducers: {
    websocketUpdates: (state, action: PayloadAction<WebosocketMessageType>) => {
      console.log("websocketUpdates method", action.payload);

      switch (action.payload.messageType) {
        case "ADD_IMAGE":
          // message.success("Получен скриншот")
          state.fullInstance?.images.unshift(action.payload.message);
          state.newImages += 1;
          break;

        case "ADD_LOGS":
          console.log("Получен архив с логами", action.payload.message);
          state.fullInstance?.logs.unshift(action.payload.message);
          state.newLogs += 1;
          console.log("state ADD_LOGS", state.newLogs);
          break;

        case "ADD_FILE":
          console.log("Получен файл от мидсервиса");
          message.success("Получен файл от мидсервиса");
          break;

        case "COMMAND_RESULT":
          console.log("COMMAND_RESULT1", action.payload);
          if (action?.payload?.message?.error != null)
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            message.error("Пришел ответ на команду");
          if (state.fullInstance == null) return;

          for (let i = 0; i < (state.fullInstance.messages.length ?? 0); i++) {
            const instanceMessages = state.fullInstance.messages;
            if (instanceMessages[i].callId === action.payload.message.callId) {
              instanceMessages[i] = {
                ...action.payload.message,
                authorLogin: instanceMessages[i].authorLogin,
                isCollapseOpen: true,
              };
              console.log("state.instances[i]", state.instances.content[i]);
              break;
            }
          }
          console.log("webosocket COMMAND_RESULT");
          break;
        case "INSTANCE_STATUS":
          if (state.fullInstance == null) return;
          void message.info("Получен статус устройства");
          state.fullInstance.midserviceInstance.status = action.payload.message;
          console.log("webosocket COMMAND_RESULT");
          break;
        case "NOTIFICATION":
          console.log("notification", action.payload);
          openNotification(action.payload.message as Notification);
          break;
        case "ONLINE_STATUS": {
          console.log("ONLINE_STATUS");
          const splited = action.payload.message.deviceId.toString().split("_");
          const domain = splited[0];
          const deviceId = splited[1];
          for (let i = 0; i < state.instances.content.length; i++) {
            console.log(
              "state.instances[i]",
              i,
              state.instances.content[i].deviceId,
              action.payload,
            );

            if (
              state.instances.content[i].deviceId.toString() === deviceId &&
              state.instances.content[i].domain.toString() === domain
            ) {
              state.instances.content[i].online =
                action.payload.message.onlineStatus;
              if (action?.payload?.message?.lastConnectionDatetime != null)
                state.instances.content[i].lastConnectionDatetime =
                  action.payload.message.lastConnectionDatetime;
              if (action?.payload?.message?.configHash != null)
                state.instances.content[i].configPatchHashes =
                  action.payload.message.configHash;
              console.log("state.instances[i]", state.instances.content[i]);
              break;
            }
          }
          if (state.fullInstance == null) return;
          console.log(
            "action payload",
            action.payload,
            state.fullInstance,
            Object.keys(state.fullInstance).length,
          );
          console.log(
            "state.fullInstance",
            state.fullInstance?.midserviceInstance?.deviceId?.toString(),
            state.fullInstance?.midserviceInstance?.domain?.toString(),
            deviceId,
            domain,
          );
          if (
            state.fullInstance?.midserviceInstance?.deviceId?.toString() ===
              deviceId &&
            state.fullInstance?.midserviceInstance?.domain?.toString() ===
              domain
          ) {
            state.fullInstance.midserviceInstance.online =
              action.payload.message.onlineStatus;
          }
          console.log("state.instances", state.instances);
          break;
        }
        default:
          void message.warning("Неизвестное сообщение");

          console.log("webosocket None");
          break;
      }
    },

    websocketUpdatesIssue: (
      state,
      action: PayloadAction<WebosocketMessageType>,
    ) => {
      if (state.fullIssue == null) return;
      switch (action.payload.messageType) {
        case "ISSUE_STATUS_TELEMETRY": {
          state.fullIssue.singleIssue.status = action.payload.message.status;
          state.fullIssue.comments = [
            ...state.fullIssue.comments,
            action.payload.message.comment,
          ];
          message.info("Устройство сменило статус проблемы");
          break;
        }
        case "ISSUE_REGISTER_TELEMETRY": {
          state.fullIssue.singleIssue.eventCount =
            action.payload.message.event_count;
          break;
        }
        case "ISSUE_STATUS": {
          const follower = action.payload.message.follower;
          const followerIndex = state.fullIssue.participants.findIndex(
            (participant) => participant.id === follower.id,
          );
          if (followerIndex !== -1) {
            state.fullIssue.participants[followerIndex] = follower;
          } else {
            state.fullIssue.participants.push(follower);
          }
          state.fullIssue.singleIssue.status = action.payload.message.status;
          message.info("Статус обновлен");
          break;
        }
        case "ISSUE_PARTICIPANTS": {
          const follower = action.payload.message.follower;
          const followerIndex = state.fullIssue.participants.findIndex(
            (participant) => participant.id === follower.id,
          );
          if (followerIndex !== -1) {
            state.fullIssue.participants[followerIndex] = follower;
          } else {
            state.fullIssue.participants.push(follower);
          }
          break;
        }
        case "ISSUE_COMMENTS":
          state.fullIssue.comments = [
            ...state.fullIssue.comments,
            action.payload.message.comment,
          ];
          if (action.payload.message.follower != null) {
            state.fullIssue.participants = [
              ...state.fullIssue.participants,
              action.payload.message.follower,
            ];
          }
          break;
        case "ISSUES_UPDATE":
          state.fullIssue.singleIssue.status = action.payload.message.status;
          break;
        default:
          void message.warning("Неизвестное сообщение");

          console.log("webosocket None");
          break;
      }
    },
    setInstanceStatus: (state, action: PayloadAction<InstanceStatusSchema>) => {
      if (state.fullInstance != null)
        state.fullInstance.midserviceInstance.status = action.payload;
    },
    setConfig: (state, action: PayloadAction<InstanceConfigType>) => {
      if (state.fullInstance != null)
        state.fullInstance.config = action.payload;
    },
    addMessage: (state, action: PayloadAction<MessageType>) => {
      if (state.fullInstance != null)
        state.fullInstance.messages.unshift({
          ...action.payload,
          isCollapseOpen: true,
        });
    },
    changeMessageCollapse: (state, action: PayloadAction<string>) => {
      if (state.fullInstance != null) {
        for (let i = 0; i < state.fullInstance.messages.length; i++) {
          console.log(
            "cmp",
            state.fullInstance.messages[i].callId,
            action.payload,
            state.fullInstance.messages[i].callId === action.payload,
          );
          if (state.fullInstance.messages[i].callId === action.payload) {
            state.fullInstance.messages[i].isCollapseOpen = !(
              state.fullInstance.messages[i].isCollapseOpen ?? false
            );
            console.log(
              "resres",
              state.fullInstance.messages[i].isCollapseOpen,
            );
            return;
          }
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    resetNewLogsCounter: (state) => {
      state.newLogs = 0;
    },
    resetNewImagesCounter: (state) => {
      state.newImages = 0;
    },
    setInstancesListCurrentPage: (state, action: PayloadAction<number>) => {
      state.instancesListCurrentPage = action.payload;
    },
    setIssuesListCurrentPage: (state, action: PayloadAction<number>) => {
      state.issuesListCurrentPage = action.payload;
    },
    // changeOnlineStatus: (state, action: PayloadAction<ChangeOnlineStatusProps>) => {

    // },
  },

  extraReducers: (builder) => {
    builder
      .addCase(midserviceInstansesListAsync.pending, (state) => {
        state.status = "loading";
        state.errorCode = null;
      })
      .addCase(midserviceInstansesListAsync.fulfilled, (state, action) => {
        state.status = "success";
        state.instances = action.payload;
        state.instances.content = state.instances.content.map((item) => ({
          ...item,
          key: String(item.id),
        }));
        state.instancesListCurrentPage = action.payload.pageNumber;
        state.errorCode = null;
      })
      .addCase(midserviceInstansesListAsync.rejected, (state, action) => {
        console.log("actionaction", action);
        state.status = "failed";
        state.errorCode = null;
      })
      .addCase(issuesListAsync.fulfilled, (state, action) => {
        state.status = "success";
        state.issues = action.payload;
        state.issues.content = state.issues.content.map((item) => ({
          ...item,
          key: String(item.id),
        }));
        state.issuesListCurrentPage = action.payload.pageNumber;
        state.errorCode = null;
      })
      .addCase(issuesListAsync.rejected, (state, action) => {
        console.log("actionaction", action);
        state.status = "failed";
        state.errorCode = null;
      });
    builder
      .addCase(fullIssueAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fullIssueAsync.fulfilled, (state, action) => {
        if (action.payload.singleIssue != null) {
          state.status = "success";
          state.fullIssue = action.payload;
        } else {
          state.status = "failed";
        }
        state.loading = false;
      })
      .addCase(fullIssueAsync.rejected, (state) => {
        state.status = "failed";
        state.loading = false;
      });
    builder
      .addCase(fullInstanceAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fullInstanceAsync.fulfilled, (state, action) => {
        state.status = "success";
        state.fullInstance = action.payload;
        state.loading = false;
      })
      .addCase(fullInstanceAsync.rejected, (state) => {
        state.status = "failed";
        state.loading = false;
      });

    builder.addCase(sendCommandAsync.rejected, (state) => {
      void message.error("Некорректные параметры");
    });
    builder
      .addCase(requestLogsAsync.pending, (state) => {
        state.logsStatus = "loading";
        void message.loading({ content: "Запрос отправлен", duration: 10 });
      })
      .addCase(requestLogsAsync.fulfilled, (state, action) => {
        if (state.fullInstance != null) {
          state.logsStatus = "success";
          // state.fullInstance.logs.push(action.payload);
          void message.success({ content: "Логи получены", duration: 2 });
        }
      })
      .addCase(requestLogsAsync.rejected, (state, action) => {
        state.logsStatus = "failed";
        console.log("requestLogsAsync", action.payload);
        message.warning({ content: action.payload, duration: 2 });
      });
    builder
      .addCase(sendConfigAsync.pending, (state) => {
        message.loading({ content: "Запрос отправлен", duration: 10 });
        state.sendConfigStatus = "loading";
      })
      .addCase(sendConfigAsync.fulfilled, (state, action) => {
        console.log("responce", action.payload);
        message.success({ content: action.payload, duration: 2 });

        state.sendConfigStatus = "success";
      })
      .addCase(sendConfigAsync.rejected, (state) => {
        message.warning({ content: "Ошибка", duration: 2 });
        state.sendConfigStatus = "failed";
      });
    builder
      .addCase(deleteLogsAsync.pending, (state) => {
        message.loading({ content: "Запрос отправлен", duration: 10 });
      })
      .addCase(deleteLogsAsync.fulfilled, (state, action) => {
        console.log("responce", action.payload);
        message.success({ content: action.payload, duration: 2 });
      })
      .addCase(deleteLogsAsync.rejected, (state, action) => {
        message.warning({ content: action.payload, duration: 2 });
      });

    builder
      .addCase(deleteInstanceAsync.fulfilled, (state, action) => {
        console.log("responce", action.payload);
        message.success("Устройство удалено");
        state.pageReload = !state.pageReload;
      })
      .addCase(deleteInstanceAsync.rejected, (state, action) => {
        message.warning("Ошибка при удалении");
      });
  },
});

export const selectMidservicesList = (
  state: RootState,
): PaginationResponse<MidserviceType[]> => state.midservice.instances;
export const selectIssuesList = (
  state: RootState,
): PaginationResponse<IssueType[]> => state.midservice.issues;

export const selectFullIssue = (state: RootState): FullIssueType | undefined =>
  state.midservice.fullIssue;

export const selectPageReload = (state: RootState): boolean =>
  state.midservice.pageReload;
export const selectMidservicesStatus = (state: RootState): Status =>
  state.midservice.status;
export const selectLogsStatus = (state: RootState): Status =>
  state.midservice.logsStatus;
export const sendConfigStatus = (state: RootState): Status =>
  state.midservice.sendConfigStatus;
export const selectFullMidservice = (
  state: RootState,
): FullInstanceType | undefined => state.midservice.fullInstance;

export const selectNewLogs = (state: RootState): number =>
  state.midservice.newLogs;
export const selectNewImages = (state: RootState): number =>
  state.midservice.newImages;

export const selectLoading = (state: RootState): boolean =>
  state.midservice.loading;
export const selectInstancesListCurrentPage = (state: RootState): number =>
  state.midservice.instancesListCurrentPage;

export const selectStatus = (state: RootState): Status =>
  state.midservice.status;

export const {
  setConfig,
  changeMessageCollapse,
  setInstanceStatus,
  addMessage,
  websocketUpdates,
  websocketUpdatesIssue,
  setLoading,
  setInstancesListCurrentPage,
  setIssuesListCurrentPage,
  resetNewLogsCounter,
  resetNewImagesCounter,
} = midserviceSlice.actions;

export default midserviceSlice.reducer;
